<template>
  <div>
    <BaseDialog
      append-to-body
      width="500px"
      hide-confirm
      @close="handleClose"
    >
      <div class="scanner-dialog__content">
        <p class="text-primary-100 font-bold text-md">請掃描會員卡 QR code</p>
        <!-- 隱藏的輸入框用於接收掃描器輸入 -->
        <input
          ref="scannerInputRef"
          type="text"
          class="scanner-dialog__hidden-input"
          style="ime-mode: disabled"
          lang="en"
          inputmode="latin"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          @blur="focusInput"
          @keydown="handleKeyDown"
          @submit.self.prevent
        >

        <!-- 掃描器圖片 -->
        <img
          src="@/assets/scannerGun/scanner_gun_scan.svg"
          alt="Scanner Gun"
          class="scanner-dialog__image"
        >

        <!-- 載入狀態 -->
        <div v-if="loading" class="scanner-dialog__loading">
          <i class="el-icon-loading" />
          <span>驗證中...</span>
        </div>
      </div>
    </BaseDialog>

    <SyncMemberDialog
      v-if="showSyncDialog && pendingUserData"
      append-to-body
      :user-data="pendingUserData"
      :shop-name="safeShopName"
      @close="handleSyncDialogClose"
      @success="handleSyncSuccess"
    />
  </div>
</template>

<script>
import { defineComponent, ref, nextTick, computed } from 'vue'
import { useQrCodeParser } from '@/use/qrCodeParser'
import { useScannerGun } from '@/use/useScannerGun'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useShop } from '@/use/shop'
import { useRouter } from 'vue-router/composables'
import SyncMemberDialog from '@/components/Dialog/SyncMemberDialog.vue'
import { get } from 'lodash'

export default defineComponent({
  name: 'ScannerDialog',
  components: {
    BaseDialog,
    SyncMemberDialog,
  },
  setup (props, { emit }) {
    const loading = ref(false)
    const scannerInputRef = ref(null)
    const { parseScannedContent } = useQrCodeParser()
    const { shopName } = useShop()
    const router = useRouter()
    const showSyncDialog = ref(false)
    const pendingUserData = ref(null)
    const pendingPhone = ref('')

    const safeShopName = computed(() => shopName.value || '')

    /**
     * 聚焦到隱藏的輸入框
     */
    const focusInput = () => {
      nextTick(() => {
        if (scannerInputRef.value) {
          scannerInputRef.value.focus()
        }
      })
    }

    /**
     * 關閉彈窗
     */
    const handleClose = () => {
      // 清理
      clearBuffer()
      loading.value = false

      emit('close')
    }

    const handleSyncDialogClose = () => {
      showSyncDialog.value = false
      pendingUserData.value = null
      pendingPhone.value = ''
      focusInput()
    }

    const handleSyncSuccess = () => {
      showSyncDialog.value = false
      pendingUserData.value = null
      pendingPhone.value = ''
      handleClose()
    }

    /**
     * 處理掃描完成
     */
    const handleScan = async (scannedValue) => {
      // 防止重複處理
      if (loading.value) return

      loading.value = true

      try {
        const result = await parseScannedContent(scannedValue)
        console.log('Parse result:', result)
        loading.value = false

        if (result.status === 'success') {
          window.$message.success('解析會員成功')
          handleClose()
          router.push({
            name: 'MemberProfile',
            params: { id: get(result, 'member.id') },
          })
        } else if (result.status === 'sync_required') {
          console.log('Sync required, showing dialog')
          pendingUserData.value = { user: result.user, member: null }
          pendingPhone.value = result.phone
          showSyncDialog.value = true
        } else {
          window.$message.error(result.message)
          focusInput()
        }
      } catch (error) {
        console.error('Parse error:', error)
        loading.value = false
        focusInput()
      }
    }

    // 使用掃描槍 composable
    const { scanBuffer, handleKeyDown, clearBuffer } = useScannerGun({
      onScan: handleScan,
      minLength: 8,
      inputTimeout: 150,
    })

    // 組件掛載後立即聚焦
    focusInput()

    return {
      loading,
      scanBuffer,
      scannerInputRef,
      focusInput,
      handleKeyDown,
      handleClose,
      showSyncDialog,
      pendingUserData,
      shopName,
      safeShopName,
      handleSyncDialogClose,
      handleSyncSuccess,
    }
  },
})
</script>

<style lang="postcss" scoped>
.scanner-dialog__content {
  @apply flex flex-col items-center gap-6 py-5 relative;
}

.scanner-dialog__hidden-input {
  @apply absolute top-0 left-0 w-[1px] h-[1px] opacity-0 pointer-events-none z-[-1];
}

.scanner-dialog__image {
  @apply w-[200px]  py-[40px];
}

.scanner-dialog__text {
  @apply text-[20px] font-medium text-[#2d271b] text-center leading-[1.5];
}

.scanner-dialog__loading {
  @apply flex items-center gap-3 text-[16px] text-[#6d7281];
}

.scanner-dialog__loading i {
  @apply text-[20px];
}
</style>
